import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { MAP_ZINDEX_LABEL } from "../data-styling";

/**
 * Render React components at specific positions on Google Maps
 *
 * We now are getting into serious map work, this is where things get fun and complicated.
 *
 * See writeups at:
 * - https://ember-google-maps.sandydoo.me/docs/overlays (high level overview)
 * - simple code: https://github.com/sandydoo/ember-google-maps/blob/main/addon/components/g-map/overlay.js
 * - https://developers.google.com/maps/documentation/javascript/reference/overlay-view (raw dive)
 *
 * Our setup is likely overly complex (our AI generator went a bit overboard). But it helps handle
 * what we need here.
 *
 * Key concepts to be aware of:
 * - Google maps have multiple panes of rendered data, we need to make sure we render into the right one
 *   so we get our desired behavior: https://developers.google.com/maps/documentation/javascript/reference/overlay-view#MapPanes
 * - default event handling may have to be turned off (right now we do this on a per layer basis, see
 *   LabelMarker#render for an example) but we may want to shift that into handling that here
 *
 * @param {Object} props - Component props
 * @param {google.maps.LatLng|{lat: number, lng: number}} props.position - Position on the map
 * @param {React.ReactNode} props.children - React components to render
 * @param {string} props.pane - Map pane to render in ('overlayLayer', 'overlayMouseTarget', 'floatPane', 'overlayImage', 'floatShadow', 'mapPane')
 * @param {Function} props.onDraw - Callback when overlay is drawn/redrawn
 * @param {Function} props.onAdd - Callback when overlay is added to map
 * @param {Function} props.onRemove - Callback when overlay is removed from map
 * @param {Object} props.offset - Pixel offset from position {x: number, y: number}
 * @param {boolean} props.clickable - Whether the overlay should capture click events
 * @param {Function} props.onDragEnd - Callback when overlay drag ends, receives event with latLng property
 * @param {Function} props.onClick - Callback when overlay is clicked (not dragged)
 */
export default function Overlay({
  position,
  children,
  pane = "overlayMouseTarget",
  onDraw,
  onAdd,
  onRemove,
  offset = { x: 0, y: 0 },
  clickable = true,
  onDragEnd,
  onClick,
}) {
  const map = useMap();
  const overlayRef = useRef(null);

  // Initialize and update overlay
  useEffect(() => {
    if (!map) return;

    // If overlay already exists, just update it
    if (overlayRef.current) {
      overlayRef.current.updateProps({
        position,
        children,
        pane,
        offset,
        clickable,
        onDraw,
        onAdd,
        onRemove,
      });
      return;
    }

    // Create new overlay
    const CustomOverlay = createCustomOverlay();
    const overlayInstance = new CustomOverlay({
      position,
      children,
      pane,
      offset,
      clickable,
      onDraw,
      onAdd,
      onRemove,
      onDragEnd,
      onClick,
    });

    overlayInstance.setMap(map);
    overlayRef.current = overlayInstance;

    return () => {
      if (overlayInstance) {
        overlayInstance.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [
    map,
    position,
    children,
    pane,
    offset,
    clickable,
    onDraw,
    onAdd,
    onRemove,
    onDragEnd,
    onClick,
  ]);

  // This component doesn't render anything to React DOM
  // The overlay is rendered directly to the Google Maps DOM
  return null;
}

/**
 * Factory function to create CustomOverlay class
 * This ensures google is available when the class is defined
 */
function createCustomOverlay() {
  return class CustomOverlay extends google.maps.OverlayView {
    constructor(props) {
      super();
      this.containerDiv = null;
      this.reactRoot = null;
      this.props = props;
    }

    updateProps(newProps) {
      const oldPosition = this.props.position;
      const oldChildren = this.props.children;

      this.props = newProps;

      // Redraw if position changed
      if (oldPosition !== newProps.position) {
        this.draw();
      }

      // Update content if children changed
      if (oldChildren !== newProps.children) {
        this.updateContent(newProps.children);
      }
    }

    onAdd() {
      // Create container div
      this.containerDiv = document.createElement("div");
      this.containerDiv.style.position = "absolute";
      this.containerDiv.style.zIndex = MAP_ZINDEX_LABEL;

      // Set pointer events based on clickable setting
      this.containerDiv.style.pointerEvents = this.props.clickable
        ? "auto"
        : "none";

      // Make draggable if onDragEnd is provided
      if (this.props.onDragEnd) {
        // Add draggable class and styles
        this.containerDiv.classList.add("overlay-draggable");
        this.addDragStyles();
        this.setupDragHandlers();
      } else if (this.props.onClick) {
        // Just handle clicks if only onClick is provided
        this.setupClickHandler();
      }

      // Create React root and render children
      if (this.props.children && this.containerDiv) {
        this.reactRoot = createRoot(this.containerDiv);
        this.reactRoot.render(this.props.children);
      }

      // Add to the appropriate pane
      const panes = this.getPanes();
      const pane = this.props.pane || "overlayMouseTarget";
      if (panes && panes[pane]) {
        panes[pane].appendChild(this.containerDiv);
      }

      // Call onAdd callback
      if (this.props.onAdd) {
        this.props.onAdd(this);
      }
    }

    draw() {
      if (!this.containerDiv) return;

      // Get projection
      const projection = this.getProjection();
      if (!projection) return;

      const position = this.props.position;
      if (!position) return;

      // Convert lat/lng to pixel coordinates
      const point = projection.fromLatLngToDivPixel(
        position instanceof google.maps.LatLng
          ? position
          : new google.maps.LatLng(position.lat, position.lng),
      );

      if (point) {
        const offset = this.props.offset || { x: 0, y: 0 };
        // Apply position and offset
        this.containerDiv.style.left = point.x + offset.x + "px";
        this.containerDiv.style.top = point.y + offset.y + "px";
      }

      // Call onDraw callback
      if (this.props.onDraw) {
        this.props.onDraw(this, point);
      }
    }

    onRemove() {
      // Clean up drag handlers
      if (this.cleanupDragHandlers) {
        this.cleanupDragHandlers();
        this.cleanupDragHandlers = null;
      }

      // Clean up click handlers
      if (this.cleanupClickHandler) {
        this.cleanupClickHandler();
        this.cleanupClickHandler = null;
      }

      // Clean up React root asynchronously to avoid race conditions
      if (this.reactRoot) {
        const rootToUnmount = this.reactRoot;
        this.reactRoot = null;

        // Use setTimeout to defer unmounting until after current render
        setTimeout(() => {
          try {
            rootToUnmount.unmount();
          } catch (error) {
            console.warn("Error unmounting React root:", error);
          }
        }, 0);
      }

      // Remove DOM element
      if (this.containerDiv && this.containerDiv.parentNode) {
        this.containerDiv.parentNode.removeChild(this.containerDiv);
      }

      this.containerDiv = null;

      // Call onRemove callback
      if (this.props.onRemove) {
        this.props.onRemove(this);
      }
    }

    // Get container element
    getContainer() {
      return this.containerDiv;
    }

    // Update content
    updateContent(newChildren) {
      if (this.reactRoot && this.containerDiv) {
        this.reactRoot.render(newChildren);
      }
    }

    // Add drag styles to the container
    addDragStyles() {
      if (!this.containerDiv) return;

      // Create and inject CSS for drag styles
      const styleId = "overlay-drag-styles";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .overlay-draggable:active {
            cursor: grabbing !important;
          }
          .overlay-draggable:active * {
            cursor: grabbing !important;
          }
        `;
        document.head.appendChild(style);
      }
    }

    // Setup drag event handlers
    setupDragHandlers() {
      if (!this.containerDiv) return;

      let isDragging = false;
      let hasDragged = false;
      let dragStartX = 0;
      let dragStartY = 0;
      let dragStartPosition = null;
      const dragThreshold = 2; // pixels - minimum movement to consider it a drag

      const handleMouseDown = (e) => {
        isDragging = true;
        hasDragged = false;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
        dragStartPosition = this.props.position;

        // Don't prevent default here - let clicks work normally
        // Only prevent if we actually start dragging
        e.stopPropagation(); // Still prevent map interaction
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStartX;
        const deltaY = e.clientY - dragStartY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Only start dragging if we've moved beyond the threshold
        if (!hasDragged && distance > dragThreshold) {
          hasDragged = true;
        }

        if (!hasDragged) return; // Don't update position until we're actually dragging

        // Convert pixel movement to lat/lng
        const projection = this.getProjection();
        if (!projection || !dragStartPosition) return;

        const startPoint = projection.fromLatLngToDivPixel(
          dragStartPosition instanceof google.maps.LatLng
            ? dragStartPosition
            : new google.maps.LatLng(
                dragStartPosition.lat,
                dragStartPosition.lng,
              ),
        );

        const newPoint = new google.maps.Point(
          startPoint.x + deltaX,
          startPoint.y + deltaY,
        );

        const newLatLng = projection.fromDivPixelToLatLng(newPoint);

        // Update position temporarily during drag
        this.props.position = {
          lat: newLatLng.lat(),
          lng: newLatLng.lng(),
        };

        this.draw();
      };

      const handleMouseUp = (e) => {
        if (!isDragging) return;

        isDragging = false;

        if (hasDragged) {
          // Handle drag end
          const deltaX = e.clientX - dragStartX;
          const deltaY = e.clientY - dragStartY;

          const projection = this.getProjection();
          if (!projection || !dragStartPosition) return;

          const startPoint = projection.fromLatLngToDivPixel(
            dragStartPosition instanceof google.maps.LatLng
              ? dragStartPosition
              : new google.maps.LatLng(
                  dragStartPosition.lat,
                  dragStartPosition.lng,
                ),
          );

          const newPoint = new google.maps.Point(
            startPoint.x + deltaX,
            startPoint.y + deltaY,
          );

          const newLatLng = projection.fromDivPixelToLatLng(newPoint);

          // Call onDragEnd with an event object similar to AdvancedMarker
          if (this.props.onDragEnd) {
            const dragEndEvent = {
              latLng: newLatLng,
            };
            this.props.onDragEnd(dragEndEvent);
          }
        } else {
          // Handle click (no significant drag occurred)
          if (this.props.onClick) {
            this.props.onClick(e);
          }
        }

        hasDragged = false;
      };

      // Add event listeners
      this.containerDiv.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Store cleanup function
      this.cleanupDragHandlers = () => {
        this.containerDiv?.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }

    // Setup click event handler (for non-draggable overlays)
    setupClickHandler() {
      if (!this.containerDiv) return;

      const handleClick = (e) => {
        e.stopPropagation(); // Prevent map interaction
        if (this.props.onClick) {
          this.props.onClick(e);
        }
      };

      this.containerDiv.addEventListener("click", handleClick);

      // Store cleanup function
      this.cleanupClickHandler = () => {
        this.containerDiv?.removeEventListener("click", handleClick);
      };
    }
  };
}
